/**
 * Translation fallbacks for the address book.
 *
 * Translations live in the API repository and are published through Lokalise;
 * this repository can only consume them. A key referenced here but not yet
 * created there is not an error at runtime — vue-i18n returns the key itself —
 * so the screen renders `attributes.invoice_count` as a column header, in
 * English, in a French interface, with nothing logged. That has already
 * happened once on this feature, which is why the two keys this screen still
 * needs each name an existing key to stand in for until the real one lands.
 *
 * A fallback is deliberately a near-synonym rather than a literal string: a
 * French word hard-coded here would never be translated for the other locales,
 * and would look correct enough that nobody would notice it had not been.
 *
 * @type {Record<string, string>}
 */
export const DIRECTORY_LABEL_FALLBACKS = {
  'attributes.invoice_count': 'global.invoices',
  'attributes.dossier_count': 'global.dossiers',
  'global.potential_duplicates': 'global.duplicate_list',
  'global.show_deceased': 'attributes.deceased',
  'global.show_people_without_dossier': 'global.dossiers',
  'global.merge_duplicate_group': 'button.merge',
  'global.duplicate_group_too_large': 'global.error',
  'global.duplicate_group_no_longer_duplicates': 'global.error',
  'global.merge_warning_several_deceased_dossiers': 'global.warning',
  'global.person_type_individual': 'global.person',
  'global.person_type_company': 'attributes.company'
}

/**
 * Translate a key, standing in a related one when it does not exist yet.
 *
 * vue-i18n answers a missing key with the key, which is the only signal
 * available without reaching into the i18n instance — and it is the same signal
 * whether the key is missing or merely untranslated, which is what we want.
 *
 * @param {function(string): string} translate usually `(key) => this.$t(key)`
 * @param {string} key
 * @returns {string}
 */
export function translateWithFallback(translate, key) {
  const translated = translate(key)

  if (translated !== key) {
    return translated
  }

  const fallbackKey = DIRECTORY_LABEL_FALLBACKS[key]

  return fallbackKey ? translate(fallbackKey) : translated
}

/**
 * Literal fallbacks for the merge panel's own vocabulary.
 *
 * Unlike `DIRECTORY_LABEL_FALLBACKS`, none of these eleven keys has an
 * existing near-synonym to stand in for them — "principal & doublons" or "la
 * fiche a été redirigée" name concepts this feature introduces, not ones the
 * rest of the application already has a word for. A literal is therefore the
 * lesser of two failures, exactly as `DirectoryToolbar.ARCHIVE_FALLBACK`
 * already accepts for the same reason: it will not be translated for the
 * other locales, but the alternative is rendering the raw key at the user,
 * which is the bug `translateWithFallback` exists to avoid. Reported to
 * Lokalise for creation; each is to be replaced by a real key once it lands.
 *
 * @type {Record<string, string>}
 */
export const MERGE_LABEL_FALLBACKS = {
  'global.merge_panel_title': 'Fusionner {n} contacts',
  'global.merge_panel_subtitle': '{n} fiche(s) seront archivées et redirigées',
  'global.merge_principal_and_duplicates': 'Fiche principale et doublons',
  'global.merge_add_a_record': 'Ajouter une fiche',
  'global.merge_arbitration': 'Champs à arbitrer',
  'global.merge_source': 'Origine',
  'global.merge_retained_value': 'Valeur retenue',
  'global.merge_death_block': 'Décès',
  'global.merge_nothing_to_arbitrate':
    'Rien à arbitrer : la fiche principale est déjà complète.',
  'global.merge_impact': "Aperçu de l'impact",
  'global.merge_gender_civility_mismatch':
    'La civilité et le genre choisis se contredisent.',
  'global.merge_divergent_values': 'valeurs différentes'
}

/**
 * Literal fallback for the Chift badge shown on a directory row already
 * linked to Chift, for the same reason ARCHIVE_LABEL_FALLBACKS exists.
 *
 * @type {Record<string, string>}
 */
export const CHIFT_LABEL_FALLBACKS = {
  'global.chift_linked_contact': 'Ce contact est lié à Chift'
}

/**
 * Translate a key, falling back to a literal for the Chift badge title.
 *
 * @param {function(string): string} translate usually `(key) => this.$t(key)`
 * @param {string} key
 * @returns {string}
 */
export function translateChiftLabel(translate, key) {
  const translated = translate(key)

  return translated === key
    ? (CHIFT_LABEL_FALLBACKS[key] ?? translated)
    : translated
}

/**
 * Translate a key, falling back to a literal for the eleven the merge panel
 * still needs from Lokalise.
 *
 * @param {function(string): string} translate usually `(key) => this.$t(key)`
 * @param {string} key
 * @returns {string}
 */
export function translateMergeLabel(translate, key) {
  const translated = translate(key)

  return translated === key
    ? (MERGE_LABEL_FALLBACKS[key] ?? translated)
    : translated
}

/**
 * Literal fallbacks for the archive confirmation, for the same reason
 * MERGE_LABEL_FALLBACKS exists: neither key has an existing near-synonym to
 * stand in for it.
 *
 * @type {Record<string, string>}
 */
export const ARCHIVE_LABEL_FALLBACKS = {
  'global.archive_confirm_title': 'Archiver la sélection',
  'global.archive_confirm_message':
    'Archiver ces fiches ? Elles disparaîtront du carnet d’adresses.',
  'global.archived_record_cannot_be_principal':
    'Une fiche archivée ne peut pas être choisie comme fiche principale.'
}

/**
 * Translate a key, falling back to a literal for the archive confirmation.
 *
 * @param {function(string): string} translate usually `(key) => this.$t(key)`
 * @param {string} key
 * @returns {string}
 */
export function translateArchiveLabel(translate, key) {
  const translated = translate(key)

  return translated === key
    ? (ARCHIVE_LABEL_FALLBACKS[key] ?? translated)
    : translated
}
